import { cognitiveColours, alpha } from '@cognitivecredit/ui-utils'
import { PROVIDER_SCALES, NO_RATING_LABELS } from './constants.js'

export const getAvailableProviders = (ratings) =>
  new Set(
    ratings
      .filter((r) => r.provider && r.rating && r.effectiveDate && PROVIDER_SCALES[r.provider])
      .map((r) => r.provider),
  )

// Scales are ordered lowest-to-highest, so a label's numeric value is
// its 1-based position in the array. We ignore Moody's "(P)" prefix
// and S&P "(sf)" and "prelim" suffixes so that they're treated as the
// same grade as the base label.
export function ratingToValue(scale, rating) {
  rating = rating.replace(/^\(P\)| \(sf\)$| prelim$/, '')
  const index = scale.findIndex((label) => label === rating)
  return index === -1 ? undefined : index + 1
}

export function buildLabels(scale) {
  return scale.map((label, index) => ({ value: index + 1, label }))
}

export function buildChartData(ratings, provider, chartEndDate) {
  const scale = PROVIDER_SCALES[provider].scale

  const ratingssorted = ratings
    .filter((r) => r.provider === provider && r.rating && r.effectiveDate && r.firstUpdated)
    .filter((r) => ratingToValue(scale, r.rating) !== undefined || NO_RATING_LABELS.includes(r.rating))
    .sort((a, b) => new Date(a.firstUpdated) - new Date(b.firstUpdated))

  const series = []
  // The scale value NR/PIF points plot flat against — the most recent real grade seen so far.
  let lastRealRating
  if (!ratingssorted.length) {
    return series
  }
  ratingssorted.forEach((row, index) => {
    const isNoRating = NO_RATING_LABELS.includes(row.rating)

    // An NR/PIF row with no earlier real rating has no level to plot flat against — drop it.
    if (isNoRating && lastRealRating === undefined) {
      return
    }

    const rating = isNoRating ? lastRealRating : ratingToValue(scale, row.rating)
    if (!isNoRating) {
      lastRealRating = rating
    }

    series.push({
      date: series.length === 0 ? new Date(row.effectiveDate) : new Date(row.firstUpdated.slice(0, 10)),
      rating,
      // Used for tooltips. Uses the original label which may include
      // NR, PIF, sf and prelim ratings
      ratingLabel: row.rating,
      // The line breaks right after an NR/PIF point, unless it's the
      // last point plotted — there's nothing after it to break.
      breakAfter: isNoRating && index !== ratingssorted.length - 1,
      outlook: row.outlook,
      directionOfChange: row.directionOfChange,
      watch: row.watch,
    })
  })
  const lastRatingRow = ratingssorted[ratingssorted.length - 1]
  const lastRatingIsNoRating = NO_RATING_LABELS.includes(lastRatingRow.rating)
  if (!lastRatingIsNoRating) {
    // Last element isn't an event, so don't show a symbol for it. It
    // just extends the line to the right edge of the chart.
    const lastRatingValue = ratingToValue(scale, lastRatingRow.rating)
    series.push({
      date: chartEndDate,
      ratingLabel: lastRatingRow.rating,
      rating: lastRatingValue,
      symbol: 'none',
    })
  }
  return series
}
export function buildLabelByValue(allLabels) {
  return Object.fromEntries(allLabels.map((item) => [item.value, item.label]))
}

// Picks which grades actually get an axis position this render — every grade marked
export function buildVisibleLabels(allLabels, seriesValues, zeroBased) {
  const usedValues = seriesValues.filter(Number.isFinite)
  let filtered

  if (zeroBased) {
    filtered = [...allLabels]
  } else {
    const min = usedValues.length ? Math.min(...usedValues) : allLabels[0].value
    const max = usedValues.length ? Math.max(...usedValues) : allLabels[allLabels.length - 1].value
    // The plus one and minus one here are to ensure that the axis
    // shows ratings one notch above and below the actual ratings
    // used, so that the line doesn't run right up to the edge of the
    // chart.
    filtered = allLabels.filter((item) => item.value >= min - 1 && item.value <= max + 1)
  }

  return filtered.map((item, index) => ({ value: index + 1, label: item.label, rawValue: item.value }))
}

export function buildRawValueMap(visibleLabels) {
  return Object.fromEntries(visibleLabels.map((item) => [item.rawValue, item.value]))
}

export function buildYAxis(visibleLabels) {
  const labelByValue = buildLabelByValue(visibleLabels)

  return {
    type: 'value',
    min: 1,
    max: visibleLabels.length,
    interval: 1,
    splitLine: { lineStyle: { color: cognitiveColours.porcelain } },
    axisLine: { show: true, lineStyle: { color: alpha(cognitiveColours.blackPearl, 0.65) } },
    axisLabel: {
      show: true,
      formatter: (val) => {
        const rounded = Math.round(val)
        return Math.abs(val - rounded) < 0.01 ? (labelByValue[rounded] ?? '') : ''
      },
    },
  }
}

export function camelifyRatingsApiRow(r) {
  return {
    provider: r.provider,
    rating: r.rating,
    effectiveDate: r.effective_date,
    firstUpdated: r.first_updated,
    outlook: r.outlook,
    directionOfChange: r.direction_of_change,
    watch: r.watch,
  }
}
